import { useCallback, useEffect, useRef, type PointerEvent } from 'react';
import type { NormalizedPoint } from '../model';
import type { PaperSize } from '../domain/geometry';
import type { ViewportTransform } from '../domain/viewport';

/** Keep the last canvas pointer target for toolbar paste, without rerendering on hover.
 * Reproject after zoom/pan so keyboard paste also works without another pointer movement.
 * With no pointer visit yet, use the center of the visible canvas.
 */
export function usePaperPastePosition(
  viewport: ViewportTransform,
  size: PaperSize,
  onChange?: (point: NormalizedPoint) => void
) {
  const lastScreenPoint = useRef<NormalizedPoint | null>(null);
  const report = useCallback(() => {
    if (size.width <= 1 || size.height <= 1) return;
    const point = lastScreenPoint.current ?? { x: size.width / 2, y: size.height / 2 };
    // Do not clamp: a panned viewport can legitimately show coordinates outside the original page.
    onChange?.({
      x: (point.x - viewport.panX) / viewport.zoom / size.width,
      y: (point.y - viewport.panY) / viewport.zoom / size.height
    });
  }, [onChange, size.width, size.height, viewport.panX, viewport.panY, viewport.zoom]);
  useEffect(report, [report]);
  return useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const point = {
        x: event.clientX - rect.left - event.currentTarget.clientLeft,
        y: event.clientY - rect.top - event.currentTarget.clientTop
      };
      // Pointer capture may deliver moves outside the paper during a drag.
      if (
        point.x < 0 ||
        point.y < 0 ||
        point.x > event.currentTarget.clientWidth ||
        point.y > event.currentTarget.clientHeight
      )
        return;
      lastScreenPoint.current = point;
      report();
    },
    [report]
  );
}
